import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Spinner, Badge } from 'react-bootstrap';
import { Table, Input, Button, Space, Typography, Select, DatePicker as AntDatePicker } from 'antd';
import { paymentReporListGet } from '../../../redux/actions/user';
import moment from "moment";
import * as XLSX from 'xlsx';
import { deleteCookieCredentials } from '../../../util/helper';
import { Download, Redo, Search } from "lucide-react";

const { Text } = Typography;
const { RangePicker } = AntDatePicker;

const filterOptions = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "Last Week", value: "Last Week" },
  { label: "This Month", value: "This Month" },
  { label: "Last Month", value: "Last Month" },
  { label: "This Quarter", value: "This Quarter" },
  { label: "Since Launch", value: "Since Launch" }
];

const getDateRange = (filterValue) => {
  const fmt = "YYYY-MM-DD";
  const m = moment;
  switch (filterValue) {
    case "Today": return [m().format(fmt), m().format(fmt)];
    case "This Week": return [m().startOf('week').format(fmt), m().endOf('week').format(fmt)];
    case "Last Week": return [m().subtract(1, 'weeks').startOf('week').format(fmt), m().subtract(1, 'weeks').endOf('week').format(fmt)];
    case "This Month": return [m().startOf('month').format(fmt), m().endOf('month').format(fmt)];
    case "Last Month": return [m().subtract(1, 'month').startOf('month').format(fmt), m().subtract(1, 'month').endOf('month').format(fmt)];
    case "This Quarter": return [m().startOf('quarter').format(fmt), m().endOf('quarter').format(fmt)];
    case "Since Launch": return ["2024-04-01", m().format(fmt)];
    default: return [m().format(fmt), m().format(fmt)];
  }
};

const EXCLUDED_EMAILS = new Set(["gsakthidoss96@gmail.com"]);

const isDiscover = (t) => t === 'd' || t === 'd-new';
const isQuikTrade = (t) => t === 'qt' || t === 'qt-new';

const getValidityInfo = (packageEndTime) => {
  const days = moment(packageEndTime).diff(moment(), 'days');
  if (isNaN(days)) return { status: 'N/A', bg: 'secondary', days: 0 };
  if (days >= 1) return { status: 'Valid', bg: 'success', days };
  return { status: 'Expired', bg: 'danger', days };
};

const getColumnSearchProps = (dataIndex, title, customValueFn) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        placeholder={`Search ${title}`}
        value={selectedKeys[0] || ''}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
        style={{ marginBottom: 8, display: 'block' }}
        autoFocus
      />
      <Space>
        <Button onClick={() => { clearFilters(); confirm(); }} size="small" style={{ width: 90 }}>
          Reset
        </Button>
        <Button type="primary" onClick={() => confirm()} icon={<Search size={14} />} size="small" style={{ width: 90 }}>
          Search
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered) => <Search size={14} style={{ color: filtered ? '#1890ff' : undefined }} />,
  onFilter: (value, record) => {
    const cellValue = customValueFn ? customValueFn(record) : String(record[dataIndex] ?? '');
    return cellValue.toLowerCase().includes(String(value).toLowerCase());
  },
});

const getColumnRangeProps = (dataIndex) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Space direction="vertical">
        <Input
          placeholder="Min" type="number" style={{ width: 150 }}
          value={selectedKeys[0]?.[0] ?? ''}
          onChange={e => setSelectedKeys([[e.target.value, selectedKeys[0]?.[1] ?? '']])}
        />
        <Input
          placeholder="Max" type="number" style={{ width: 150 }}
          value={selectedKeys[0]?.[1] ?? ''}
          onChange={e => setSelectedKeys([[selectedKeys[0]?.[0] ?? '', e.target.value]])}
        />
        <Space>
          <Button onClick={() => { clearFilters(); confirm(); }} size="small" style={{ width: 70 }}>Reset</Button>
          <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 70 }}>Filter</Button>
        </Space>
      </Space>
    </div>
  ),
  onFilter: (value, record) => {
    const val = Number(record[dataIndex]);
    const min = value?.[0] !== '' && value?.[0] != null ? Number(value[0]) : null;
    const max = value?.[1] !== '' && value?.[1] != null ? Number(value[1]) : null;
    if (min !== null && val < min) return false;
    if (max !== null && val > max) return false;
    return true;
  },
});

const getColumnDateRangeProps = (dataIndex) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <RangePicker
        value={selectedKeys[0] || null}
        onChange={values => setSelectedKeys(values ? [values] : [])}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button onClick={() => { clearFilters(); confirm(); }} size="small" style={{ width: 90 }}>Reset</Button>
        <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>Filter</Button>
      </Space>
    </div>
  ),
  onFilter: (value, record) => {
    if (!value || value.length < 2 || !value[0] || !value[1]) return true;
    return moment(record[dataIndex]).isBetween(value[0], value[1], 'day', '[]');
  },
});

const PaymentUsers = ({ type }) => {
  const navigate = useNavigate();
  const userSrc = useSelector(state => state.user.src);

  const [userList, setUserList] = useState([]);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [filterValue, setFilterValue] = useState("Today");
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (type) { setFilterValue(type); setDateRange(null); }
  }, [type]);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      let start_date, end_date;
      if (dateRange?.[0] && dateRange?.[1]) {
        start_date = dateRange[0].format("YYYY-MM-DD");
        end_date = dateRange[1].format("YYYY-MM-DD");
      } else {
        [start_date, end_date] = getDateRange(filterValue);
      }

      const data = await paymentReporListGet(userSrc, start_date, end_date, false);
      if (cancelled) return;

      if (data?.sessionExpired) {
        deleteCookieCredentials();
        navigate("/authentication/login");
        return;
      }

      if (data) {
        const seen = new Map();
        const filtered = (data.users || []).filter(item => {
          if (EXCLUDED_EMAILS.has(item.email)) {
            return false;
          }
          if (seen.has(item.id)) {
            return false;
          }
          seen.set(item.id, item);
          return true;
        });
        setUserList(filtered);
        setCurrentTableData(filtered);
      }
      setLoading(false);
    }

    if (filterValue || (dateRange?.[0] && dateRange?.[1])) fetchData();
    return () => { cancelled = true; };
  }, [filterValue, dateRange, userSrc]);

  const exportToExcel = () => {
    const dataToExport = currentTableData.map(item => ({
      'Name': `${item.firstName} ${item.lastName}`,
      'Email': item.email,
      'Mobile': item.mobile,
      'Product Type': isDiscover(item.productType) ? 'Discover' : 'QuikTrade',
      'Plan': item.planName,
      'Start Date': moment(item.packageStartTime).format("DD-MMM-YYYY"),
      'End Date': moment(item.packageEndTime).format("DD-MMM-YYYY"),
      'Validity': item.validDays,
      'Amount': item.amount,
      'Expiring In': getValidityInfo(item.packageEndTime).days + ' Days',
      'Status': item.status,
      'Created At': moment(item.time).format("DD-MMM-YYYY"),
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, `Payment_Report_${moment().format('YYYY-MM-DD')}.xlsx`);
  };

  const columns = [
    {
      title: 'Name', key: 'name', width: 200,
      render: (_, r) => `${r.firstName} ${r.lastName}`,
      sorter: (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
      ...getColumnSearchProps('name', 'Name', (r) => `${r.firstName ?? ''} ${r.lastName ?? ''}`),
    },
    {
      title: 'Email', dataIndex: 'email', key: 'email', width: 220,
      render: (email) => <a href={`mailto:${email}`}>{email}</a>,
      ...getColumnSearchProps('email', 'Email', (r) => r.email ?? ''),
    },
    {
      title: 'Mobile', dataIndex: 'mobile', key: 'mobile', width: 150,
      ...getColumnSearchProps('mobile', 'Mobile'),
    },
    {
      title: 'Product Type', dataIndex: 'productType', key: 'productType', width: 150,
      render: (t) =>
        isDiscover(t) ? <span className="quik">Discover</span> :
          isQuikTrade(t) ? <><span className="quik">Quik</span><span className="trade">Trade</span></> : '',
      filters: [
        { text: 'Discover', value: 'd' },
        { text: 'QuikTrade', value: 'qt' },
      ],
      onFilter: (value, record) =>
        value === 'd' ? isDiscover(record.productType) : isQuikTrade(record.productType),
    },
    {
      title: 'Plan', dataIndex: 'planName', key: 'planName', width: 120,
      render: (plan) => <span className="text-capitalize">{plan}</span>,
      ...getColumnSearchProps('planName', 'Plan'),
    },
    {
      title: 'Start Date', dataIndex: 'packageStartTime', key: 'packageStartTime', width: 140,
      render: (d) => moment(d).format("DD-MMM-YYYY"),
      sorter: (a, b) => moment(a.packageStartTime).unix() - moment(b.packageStartTime).unix(),
    },
    {
      title: 'End Date', dataIndex: 'packageEndTime', key: 'packageEndTime', width: 140,
      render: (d) => moment(d).format("DD-MMM-YYYY"),
      sorter: (a, b) => moment(a.packageEndTime).unix() - moment(b.packageEndTime).unix(),
    },
    {
      title: 'Validity', dataIndex: 'validDays', key: 'validDays', width: 100,
      sorter: (a, b) => a.validDays - b.validDays,
    },
    {
      title: 'Amount', dataIndex: 'amount', key: 'amount', width: 100, align: 'right',
      sorter: (a, b) => a.amount - b.amount,
      ...getColumnRangeProps('amount'),
    },
    {
      title: 'Expiring In', key: 'expiringIn', width: 120,
      render: (_, r) => { const { days } = getValidityInfo(r.packageEndTime); return `${days} ${days === 1 ? 'Day' : 'Days'}`; },
      sorter: (a, b) => moment(a.packageEndTime).diff(moment()) - moment(b.packageEndTime).diff(moment()),
    },
    {
      title: 'Validity Status', key: 'validityStatus', width: 130,
      render: (_, r) => { const { status, bg } = getValidityInfo(r.packageEndTime); return <Badge bg={bg} className="soft-ba">{status}</Badge>; },
      filters: [
        { text: 'Valid', value: 'Valid' },
        { text: 'Expired', value: 'Expired' },
        { text: 'N/A', value: 'N/A' },
      ],
      onFilter: (value, record) => getValidityInfo(record.packageEndTime).status === value,
    },
    {
      title: 'Payment Status', dataIndex: 'status', key: 'status', width: 130,
      render: (status) => (
        <Badge bg={status === 'success' ? 'success' : 'danger'} className="soft-ba text-capitalize">{status}</Badge>
      ),
      filters: [
        { text: 'Success', value: 'success' },
        { text: 'Failed', value: 'failed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Created At', dataIndex: 'time', key: 'time', width: 140,
      render: (t) => moment(t).format("DD-MMM-YYYY"),
      sorter: (a, b) => moment(a.time).unix() - moment(b.time).unix(),
    },
  ];

  return (
    <div className="payment-users-container">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
          <Space style={{ alignItems: 'end' }} size="middle" wrap>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text style={{ color: '#9da9bb', marginBottom: 4 }}>Filter By</Text>
              <Select
                options={filterOptions} style={{ width: 150 }} value={filterValue}
                onChange={(val) => { setFilterValue(val); setDateRange(null); }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text style={{ color: '#9da9bb', marginBottom: 4 }}>Custom Range</Text>
              <RangePicker
                value={dateRange} format="YYYY-MM-DD"
                onChange={(dates) => { setDateRange(dates); setFilterValue(null); }}
              />
            </div>
            <Text style={{ color: '#9da9bb', paddingBottom: 4 }}>
              {currentTableData.length} records found
            </Text>
          </Space>
          <Space>
            <Button type="primary" icon={<Download size={16} />} onClick={exportToExcel} disabled={currentTableData.length === 0}>
              Download Excel
            </Button>
            <Button icon={<Redo size={16} />} onClick={() => { setFilterValue("Today"); setDateRange(null); }}>
              Reset Filters
            </Button>
          </Space>
        </div>

        {loading ? (
          <div style={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={userList}
            rowKey={(record, index) => record.id ?? index}
            scroll={{ x: 1500, y: 'calc(100vh - 360px)' }}
            size="small"
            className="custom-antd-table payment-users-table"
            pagination={false}
            onChange={(_pagination, _filters, _sorter, extra) => {
              setCurrentTableData(extra.currentDataSource);
            }}
          />
        )}
      </Space>
    </div>
  );
};

export default PaymentUsers;